<template>
  <PageLayout title="Follow Your Heart">

    <!-- ========== 移动端模板：单题模式 ========== -->
    <div v-if="isMobile" class="mobile-mbti">
      <!-- 移动端：介绍页 -->
      <div v-if="!testStarted" class="mobile-intro">
        <h3 class="mobile-intro-title">了解你的性格类型</h3>
        <p class="mobile-intro-text">
          MBTI 帮助你更好地理解自己。通过回答一系列问题，发现你的性格倾向。
        </p>

        <div class="mobile-notice-box">
          <p>
            MBTI 测试结果反映的是你当前的性格倾向。随着时间推移，你的性格特质也会改变。请不要让任何测试结果限制了你的人生选择。
          </p>
        </div>

        <div class="mobile-dimension-list">
          <div
            v-for="dim in MBTI.dimensions"
            :key="dim.id"
            class="mobile-dimension-item"
          >
            <span class="mobile-dim-name">{{ dim.name }}</span>
            <div class="mobile-dim-poles">
              <span>{{ dim.poles[0] }}</span>
              <span class="mobile-vs">vs</span>
              <span>{{ dim.poles[1] }}</span>
            </div>
          </div>
        </div>

        <button class="mobile-start-btn" @click="mobileStartTest">
          开始测试
        </button>
      </div>

      <!-- 移动端：答题页（单题模式） -->
      <div v-else-if="!testFinished" class="mobile-testing">
        <!-- 进度条 -->
        <div class="mobile-progress-header">
          <div class="mobile-progress-text">
            {{ answeredCount }}/{{ MBTI.questions.length }}
          </div>
          <div class="mobile-progress-bar">
            <div
              class="mobile-progress-fill"
              :style="{ width: progressPercent + '%' }"
            />
          </div>
          <div class="mobile-dimension-tag">
            {{ getDimensionName(MBTI.questions[currentQuestionIndex]?.dimension) }}维度
          </div>
        </div>

        <!-- 题目内容（带过渡动画） -->
        <div class="mobile-question-area">
          <transition :name="slideDirection" mode="out-in">
            <div
              :key="currentQuestionIndex"
              class="mobile-question-content"
            >
              <div class="mobile-question-number">
                第 {{ currentQuestionIndex + 1 }} 题
              </div>
              <p class="mobile-question-text">
                {{ MBTI.questions[currentQuestionIndex]?.text }}
              </p>

              <!-- 选项A -->
              <div class="mobile-option-label mobile-option-a">
                A: {{ MBTI.questions[currentQuestionIndex]?.optionA }}
              </div>

              <!-- 5档选择按钮 -->
              <div class="mobile-scale-buttons">
                <button
                  v-for="(opt, i) in scaleOptions"
                  :key="opt.value"
                  class="mobile-scale-btn"
                  :class="{
                    'is-selected': answers[currentQuestionIndex] === opt.value
                  }"
                  :style="{ animationDelay: i * 40 + 'ms' }"
                  @click="selectScale(opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>

              <!-- 选项B -->
              <div class="mobile-option-label mobile-option-b">
                B: {{ MBTI.questions[currentQuestionIndex]?.optionB }}
              </div>
            </div>
          </transition>
        </div>

        <!-- 固定底部导航 -->
        <div class="mobile-nav-bar">
          <button
            class="mobile-nav-btn mobile-nav-prev"
            :disabled="currentQuestionIndex === 0"
            @click="goPrev"
          >
            ◀ 上一题
          </button>
          <button
            class="mobile-nav-btn mobile-nav-next"
            :disabled="currentQuestionIndex >= MBTI.questions.length - 1"
            @click="goNext"
          >
            下一题 ▶
          </button>
        </div>
      </div>

      <!-- 移动端：结果页 -->
      <div v-else class="mobile-result">
        <div class="mobile-result-type-badge">
          {{ result.type }}
        </div>
        <p class="mobile-result-subtitle">你的MBTI性格类型</p>

        <div class="mobile-result-notice">
          <p>
            这份结果展示你目前的性格倾向。性格类型不是永恒不变的，
            随着人生阅历丰富和自我认知提升，你的特质会不断发展。
          </p>
        </div>

        <!-- 维度强度条 -->
        <div class="mobile-dimension-bars">
          <div
            v-for="dim in MBTI.dimensions"
            :key="dim.id"
            class="mobile-dim-bar-item"
          >
            <div class="mobile-dim-bar-labels">
              <span>{{ dim.poles[0] }}</span>
              <span>{{ dim.poles[1] }}</span>
            </div>
            <div class="mobile-dim-bar-track">
              <div
                class="mobile-dim-bar-fill"
                :style="{ width: getDimBarWidth(dim.id) + '%' }"
              />
            </div>
            <div class="mobile-dim-bar-percent">
              {{ getDimPercent(dim.id) }}%
            </div>
          </div>
        </div>

        <TypeAnalysis :type="result.type" />

        <div class="mobile-result-actions">
          <button class="mobile-retake-btn" @click="mobileRetakeTest">
            重新测试
          </button>
          <button class="mobile-poster-btn" @click="showPoster = true">
            生成海报
          </button>
          <button class="mobile-poster-btn" @click="shareVisible = true" style="background:#e60023;color:#fff">
            📣 分享结果
          </button>
        </div>
      </div>
    </div>

    <!-- ========== PC端模板（保持不变） ========== -->
    <template v-else>
      <!-- 测试介绍 -->
      <MysticalCard v-if="!testStarted" class="intro-card">
        <h3>了解你的性格类型</h3>
        <p class="intro-text">
          MBTI (迈尔斯-布里格斯类型指标) 是一个帮助人们更好地理解自己和他人的工具。
          通过回答一系列问题，你可以发现自己的性格倾向，从而更好地认识自己。
        </p>
        
        <div class="notice-box">
          <h4>温馨提示</h4>
          <p>
            MBTI 测试结果反映的是你当前的性格倾向。随着时间推移、认知发展以及环境变化，
            你的性格特质也会随之改变。这个测试的目的是帮助你更好地了解当下的自己，
            而不是给自己贴上永久的标签。每个人都有无限的成长可能，请不要让任何测试结果限制了你的人生选择。
          </p>
        </div>

        <div class="dimension-grid">
          <div 
            v-for="dim in MBTI.dimensions" 
            :key="dim.id"
            class="dimension-card"
          >
            <h4>{{ dim.name }}</h4>
            <div class="poles">
              <span>{{ dim.poles[0] }}</span>
              <span class="vs">vs</span>
              <span>{{ dim.poles[1] }}</span>
            </div>
            <p>{{ dim.description }}</p>
          </div>
        </div>

        <div class="button-container">
          <MysticalButton 
            @click="startTest"
            size="large"
            class="start-button"
          >
            开始测试
          </MysticalButton>
        </div>
      </MysticalCard>

      <!-- 问题列表 -->
      <div v-else-if="!testFinished" class="questions-section">
        <MysticalCard class="questions-card">
          <h3>MBTI 性格测试问卷</h3>
          <p class="instruction">
            请根据你的真实想法，选择最符合你的选项。
            每个问题的选项从左到右分别代表：
          </p>
          <div class="scale-legend">
            <span>非常A</span>
            <span>偏A</span>
            <span>中立</span>
            <span>偏B</span>
            <span>非常B</span>
          </div>

          <form @submit.prevent="submitTest" class="questions-form">
            <div 
              v-for="(question, index) in MBTI.questions" 
              :key="question.id"
              class="question-item"
            >
              <div class="question-header">
                <div class="question-id">第 {{ question.id }} 题</div>
                <h4 class="question-text">{{ question.text }}</h4>
              </div>

              <div class="options-container">
              <div class="option-labels">
                <span class="option-a"><span class="option-tag option-tag-a">A</span> {{ question.optionA }}</span>
                <span class="option-b">{{ question.optionB }} <span class="option-tag option-tag-b">B</span></span>
              </div>

                <div class="scale-options">
                  <label 
                    v-for="option in scaleOptions" 
                    :key="option.value"
                    class="scale-option"
                    :class="{ 
                      'selected': answers[index] === option.value,
                      [option.type]: true 
                    }"
                  >
                    <input 
                      type="radio" 
                      :name="`question-${index}`"
                      :value="option.value"
                      v-model="answers[index]"
                      required
                    >
                    <span class="option-label">{{ option.label }}</span>
                  </label>
                </div>
              </div>

              <div 
                v-if="index % 23 === 22" 
                class="dimension-separator"
              >
                <span class="separator-text">
                  {{ getDimensionName(question.dimension) }}维度结束
                </span>
              </div>
            </div>

            <div class="form-actions">
              <p class="validation-message" v-if="showValidationMessage">
                请确保所有问题都已回答
              </p>
              <MysticalButton 
                type="submit"
                size="large"
                :loading="isSubmitting"
              >
                提交测试
              </MysticalButton>
            </div>
          </form>
        </MysticalCard>
      </div>

      <!-- 测试结果 -->
      <div v-else class="result-section">
        <MysticalCard class="result-notice">
          <div class="notice-content">
            <h4>关于测试结果的说明</h4>
            <p>
              这份测试结果展示了你目前的性格倾向，它可以帮助你：
            </p>
            <ul>
              <li>更好地认识当下的自己</li>
              <li>了解自己的优势和潜在发展方向</li>
              <li>在职业和人际关系中做出更明智的选择</li>
            </ul>
            <p class="important-note">
              请记住：性格类型不是永恒不变的。随着人生阅历的丰富和自我认知的提升，
              你的性格特质会不断发展和完善。这个结果仅供参考，真正重要的是持续的自我探索和成长。
            </p>
          </div>
        </MysticalCard>

        <TypeAnalysis :type="result.type" />

        <div class="result-actions">
          <MysticalButton @click="retakeTest">
            重新测试
          </MysticalButton>
          <MysticalButton @click="showPoster = true">
            生成海报
          </MysticalButton>
          <MysticalButton @click="shareVisible = true">
            📣 分享结果
          </MysticalButton>
        </div>
      </div>
    </template>

    <PosterDialog v-model="showPoster" type="mbti" :data="posterData" />
    <ShareResult v-model:visible="shareVisible" scene-label="MBTI人格测试"
      :headline="`我是${mbtiNames[result?.type] || '人格'} ${result?.type || ''}`"
      :subline="'MBTI人格测试 · 认识你自己'"
      :tags="[result?.type?.[0]==='E'?'外向E':'内向I', result?.type?.[1]==='S'?'实感S':'直觉N', result?.type?.[2]==='T'?'理性T':'感性F', result?.type?.[3]==='J'?'计划J':'知觉P']"
      page="mbti" />
  </PageLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { MBTI } from '@/data/mbti'
import PageLayout from '@/components/layout/PageLayout.vue'
import { ShareResult } from '@/components/common'
import MysticalCard from '@/components/common/MysticalCard.vue'
import MysticalButton from '@/components/common/MysticalButton.vue'
import TypeAnalysis from '@/components/mbti/TypeAnalysis.vue'
import PosterDialog from '@/components/poster/PosterDialog.vue'
import { cookieUtils } from '@/utils/cookie'
import { ElMessage } from 'element-plus'
import { API_CONFIG } from '@/config'
import { useMobile } from '@/composables/useMobile'
import { useAuthGuard } from '@/composables/useAuthGuard'

const { isMobile } = useMobile()
const { requireLogin } = useAuthGuard()

// ========== 共享状态（PC/移动端复用） ==========
const testStarted = ref(false)
const testFinished = ref(false)
const showValidationMessage = ref(false)
const isSubmitting = ref(false)
const result = ref(null)
const shareVisible = ref(false)
const mbtiNames = { INTJ:'建筑师', INTP:'逻辑学家', ENTJ:'指挥官', ENTP:'辩论家', INFJ:'提倡者', INFP:'调停者', ENFJ:'主人公', ENFP:'竞选者', ISTJ:'物流师', ISFJ:'守卫者', ESTJ:'总经理', ESFJ:'执政官', ISTP:'鉴赏家', ISFP:'探险家', ESTP:'企业家', ESFP:'表演者' }
const showPoster = ref(false)
const answers = ref(new Array(MBTI.questions.length).fill(null))

// ========== 移动端专用状态 ==========
const currentQuestionIndex = ref(0)
const slideDirection = ref('slide-left')

// ========== 5档量表选项（兼容现有计分） ==========
const scaleOptions = [
  { value: 'strongA', label: '非常A', type: 'strong-a' },
  { value: 'moderateA', label: '偏A', type: 'moderate-a' },
  { value: 'neutral', label: '中立', type: 'neutral' },
  { value: 'moderateB', label: '偏B', type: 'moderate-b' },
  { value: 'strongB', label: '非常B', type: 'strong-b' }
]

// ========== 计算属性 ==========
const answeredCount = computed(() => {
  return answers.value.filter(a => a !== null).length
})

const progressPercent = computed(() => {
  return Math.round((answeredCount.value / MBTI.questions.length) * 100)
})

const MBTI_TYPE_NAMES = {
  'ISTJ': '检查者', 'ISFJ': '守护者', 'INFJ': '咨询师', 'INTJ': '建筑师',
  'ISTP': '工匠', 'ISFP': '艺术家', 'INFP': '调停者', 'INTP': '逻辑学家',
  'ESTP': '企业家', 'ESFP': '表演者', 'ENFP': '竞选者', 'ENTP': '辩论家',
  'ESTJ': '总经理', 'ESFJ': '执政官', 'ENFJ': '主人公', 'ENTJ': '指挥官'
}

const posterData = computed(() => {
  if (!result.value) return {}
  const s = result.value.scores || {}
  return {
    mbtiType: result.value.type || '',
    typeName: MBTI_TYPE_NAMES[result.value.type] || result.value.type || '',
    description: result.value.analysis || '',
    eiPct: s.EI ? String(Math.round(s.EI.preference * 100)) : '50',
    snPct: s.SN ? String(Math.round(s.SN.preference * 100)) : '50',
    tfPct: s.TF ? String(Math.round(s.TF.preference * 100)) : '50',
    jpPct: s.JP ? String(Math.round(s.JP.preference * 100)) : '50',
    qrcodeUrl: window.location.href
  }
})

// ========== 共享生命周期 ==========
onMounted(async () => {
  const savedAnswers = cookieUtils.getCookie('mbti_answers')
  if (savedAnswers) {
    try {
      answers.value = JSON.parse(savedAnswers)
      if (answers.value.length === MBTI.questions.length && !answers.value.includes(null)) {
        // 所有题已答完，直接显示结果
        submitTest(true)
      } else if (answers.value.some(a => a !== null)) {
        testStarted.value = true
        // 移动端：恢复到第一道未答题目
        const firstUnanswered = answers.value.findIndex(a => a === null)
        if (firstUnanswered >= 0) {
          currentQuestionIndex.value = firstUnanswered
        }
      }
    } catch (e) {
      console.error('Failed to load saved answers:', e)
    }
  }
})

// ========== PC端方法 ==========
const startTest = () => {
  cookieUtils.deleteCookie('mbti_answers')
  cookieUtils.deleteCookie('mbti_result')
  answers.value = new Array(MBTI.questions.length).fill(null)
  testStarted.value = true
  testFinished.value = false
  result.value = null
}

const submitTest = async (isRestoring = false) => {
  if (answers.value.some(answer => answer === null)) {
    showValidationMessage.value = true
    // PC端滚动到未答题
    if (!isMobile.value) {
      const firstUnanswered = answers.value.findIndex(answer => answer === null)
      document.querySelectorAll('.question-item')[firstUnanswered]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    return
  }

  isSubmitting.value = true
  try {
    cookieUtils.setCookie('mbti_answers', JSON.stringify(answers.value), 30)
    
    const analysisResult = MBTI.analysis.calculateType(answers.value)
    if (analysisResult.error) throw new Error(analysisResult.error)
    
    result.value = { type: analysisResult.type, scores: analysisResult.scores, analysis: analysisResult.analysis }
    testFinished.value = true
    
    cookieUtils.setCookie('mbti_result', JSON.stringify({ 
      type: analysisResult.type, 
      scores: { 
        ei: analysisResult.scores.EI?.preference ?? 0.5, 
        sn: analysisResult.scores.SN?.preference ?? 0.5, 
        tf: analysisResult.scores.TF?.preference ?? 0.5, 
        jp: analysisResult.scores.JP?.preference ?? 0.5 
      } 
    }), 30)
    
    if (!isRestoring) {
      await saveResultToBackend(analysisResult.type, analysisResult.scores)
    }
    
  } catch (error) {
    console.error('计算结果出错:', error)
    ElMessage.error('计算结果出错，请稍后重试')
  } finally {
    isSubmitting.value = false
  }
}

const retakeTest = () => {
  cookieUtils.deleteCookie('mbti_answers')
  cookieUtils.deleteCookie('mbti_result')
  answers.value = new Array(MBTI.questions.length).fill(null)
  testStarted.value = false
  testFinished.value = false
  result.value = null
}

const saveResultToBackend = async (mbtiType, scores) => {
  if (!requireLogin('save-mbti')) return
  const token = localStorage.getItem('token')
  if (!token) return
  
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.mbti.save}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        mbti_type: mbtiType,
        scores: {
          ei: scores.EI?.preference ?? 0.5,
          sn: scores.SN?.preference ?? 0.5,
          tf: scores.TF?.preference ?? 0.5,
          jp: scores.JP?.preference ?? 0.5
        },
        answers: answers.value
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `保存失败: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    if (data.success) {
      ElMessage.success('测试结果已保存')
    } else {
      throw new Error(data.message || '保存失败')
    }
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(error.message || '保存测试结果失败，请稍后重试')
  }
}

// ========== 移动端专用方法 ==========
const mobileStartTest = () => {
  cookieUtils.deleteCookie('mbti_answers')
  cookieUtils.deleteCookie('mbti_result')
  answers.value = new Array(MBTI.questions.length).fill(null)
  currentQuestionIndex.value = 0
  testStarted.value = true
  testFinished.value = false
  result.value = null
}

const mobileRetakeTest = () => {
  cookieUtils.deleteCookie('mbti_answers')
  cookieUtils.deleteCookie('mbti_result')
  answers.value = new Array(MBTI.questions.length).fill(null)
  currentQuestionIndex.value = 0
  testStarted.value = false
  testFinished.value = false
  result.value = null
}

const selectScale = (value) => {
  answers.value[currentQuestionIndex.value] = value
  // 自动保存到cookie
  cookieUtils.setCookie('mbti_answers', JSON.stringify(answers.value), 30)

  // 检查是否全部答完
  if (!answers.value.includes(null)) {
    submitTest(false)
  }
}

const goPrev = () => {
  if (currentQuestionIndex.value > 0) {
    slideDirection.value = 'slide-right'
    currentQuestionIndex.value--
  }
}

const goNext = () => {
  if (currentQuestionIndex.value < MBTI.questions.length - 1) {
    slideDirection.value = 'slide-left'
    currentQuestionIndex.value++
  }
}

// ========== 维度辅助方法 ==========
const getDimensionName = (dimension) => ({ 'EI': '能量方向', 'SN': '信息获取', 'TF': '决策方式', 'JP': '生活方式' }[dimension] || dimension)

// 移动端结果页：维度条
const getDimBarWidth = (dimId) => {
  if (!result.value?.scores) return 50
  const scoreMap = { EI: 'EI', SN: 'SN', TF: 'TF', JP: 'JP' }
  const score = result.value.scores[scoreMap[dimId]]
  return score ? Math.round(score.preference * 100) : 50
}

const getDimPercent = (dimId) => {
  return getDimBarWidth(dimId)
}
</script>

<style scoped>
/* ========== PC端样式（完全保持原样） ========== */
.header-icon{font-size:36px}
.title{font-size:2rem;font-weight:700;color:var(--color-light);margin:0}
.subtitle{color:var(--color-primary);font-size:1rem;margin:0}
.intro-text{line-height:1.6;margin-bottom:var(--spacing-xl)}
.dimension-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:var(--spacing-lg);margin:var(--spacing-xl) auto;max-width:800px}
.dimension-card{background:var(--card-bg);border-radius:var(--radius-lg);padding:var(--spacing-lg)}
.dimension-card h4{color:var(--color-primary);margin-bottom:var(--spacing-md);text-align:center;font-size:1.1rem}
.dimension-card .poles{display:flex;align-items:center;justify-content:space-between;margin:var(--spacing-md) 0}
.dimension-card .poles .vs{color:var(--color-primary);font-size:0.9em}
.dimension-card p{text-align:center;color:var(--color-text-light);font-size:0.9rem}
.questions-section{max-width:900px;margin:0 auto}
.questions-card{padding:var(--spacing-xl)}
.instruction{text-align:center;margin:var(--spacing-lg) 0;color:var(--color-light);opacity:0.9}
.scale-legend{display:flex;justify-content:space-between;margin:var(--spacing-xl) 0;padding:var(--spacing-md);background:rgba(255,255,255,0.1);border-radius:var(--radius-lg)}
.question-item{margin:var(--spacing-xl) 0;padding:var(--spacing-xl);background:rgba(255,255,255,0.05);border-radius:var(--radius-lg);transition:all var(--transition-normal)}
.question-item:hover{background:rgba(255,255,255,0.08);transform:translateY(-2px)}
.question-header{display:flex;flex-direction:column;align-items:center;text-align:center;margin-bottom:var(--spacing-lg)}
.question-id{background:var(--color-primary);color:#fff;padding:4px 12px;border-radius:var(--radius-lg);font-weight:bold;margin-bottom:var(--spacing-md)}
.question-text{font-size:1.2rem;color:var(--color-light);margin:0;padding:0 var(--spacing-xl)}
.options-container{margin-top:var(--spacing-lg)}
.option-labels{display:flex;justify-content:space-between;margin-bottom:var(--spacing-md);padding:0 var(--spacing-md)}
.option-a,.option-b{flex:1;padding:var(--spacing-sm);color:var(--color-light);opacity:0.9;text-align:center;font-size:0.95rem}
.scale-options{display:flex;justify-content:space-between;gap:var(--spacing-sm);padding:0 var(--spacing-md)}
.scale-option{flex:1;display:flex;flex-direction:column;align-items:center;gap:var(--spacing-xs);padding:var(--spacing-sm);border:1px solid var(--color-border);border-radius:var(--radius-md);cursor:pointer;transition:all var(--transition-normal)}
.scale-option:hover{background:rgba(255,255,255,0.1)}
.scale-option.selected{background:var(--color-primary);border-color:var(--color-primary)}
.dimension-separator{margin:var(--spacing-xl) 0;text-align:center;position:relative}
.separator-text{background:var(--color-dark);color:var(--color-primary);padding:var(--spacing-sm) var(--spacing-lg);border-radius:var(--radius-lg);font-size:0.9rem;position:relative;z-index:1}
.dimension-separator::before{content:'';position:absolute;top:50%;left:0;right:0;height:1px;background:var(--color-primary);opacity:0.3}
.form-actions{margin-top:var(--spacing-xl);text-align:center}
.validation-message{color: var(--fh-danger);margin-bottom:var(--spacing-md)}
.notice-box{background:var(--color-bg-light);border-radius:var(--radius-lg);padding:var(--spacing-lg);margin:var(--spacing-xl) 0;border-left:4px solid var(--color-primary)}
.notice-box h4{color:var(--color-primary);margin-bottom:var(--spacing-sm);font-size:1.1rem}
.notice-box p{line-height:1.6;color:var(--color-text-light)}
.result-notice{margin-bottom:var(--spacing-xl)}
.notice-content{color:var(--color-text-light)}
.notice-content ul{margin:var(--spacing-md) 0;padding-left:var(--spacing-lg)}
.notice-content li{margin:var(--spacing-xs) 0;line-height:1.5}
.important-note{margin-top:var(--spacing-md);padding:var(--spacing-md);background:var(--color-bg-dark);border-radius:var(--radius-md);font-style:italic;color:var(--color-primary)}
.button-container{display:flex;justify-content:center;margin-top:var(--spacing-xl)}
.start-button{min-width:200px}
.result-actions{margin-top:var(--spacing-xl);text-align:center}

.option-tag {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.85em;
  margin-right: 4px;
}
.option-tag-a {
  background: var(--color-primary);
  color: #fff;
}
.option-tag-b {
  background: var(--color-primary);
  color: #fff;
}

@media (max-width: 768px) {
  .dimension-grid {
    grid-template-columns: 1fr;
  }
  .scale-options {
    flex-direction: column;
  }
  .scale-option {
    flex-direction: row;
    justify-content: space-between;
  }
  .option-labels {
    flex-direction: column;
    gap: var(--spacing-md);
  }
  .option-a,
  .option-b {
    text-align: center;
  }
}

@media (max-width: 400px) {
  .title {
    font-size: 1.5rem;
  }
  .question-text {
    font-size: 1rem;
  }
  .header-icon {
    font-size: 28px;
  }
  .scale-option {
    padding: 4px;
  }
}

/* ========== 移动端单题模式样式 ========== */
.mobile-mbti {
  min-height: 100vh;
  background: var(--mobile-bg-card);
  color: var(--mobile-text-primary);
  font-size: var(--mobile-font-base);
  line-height: var(--mobile-line-height);
  padding-bottom: var(--mobile-bottom-padding);
}

/* -- 移动端介绍页 -- */
.mobile-intro {
  padding: var(--mobile-lg) var(--mobile-xl);
}

.mobile-intro-title {
  font-size: var(--mobile-font-2xl);
  font-weight: 700;
  color: var(--mobile-text-primary);
  margin: 0 0 var(--mobile-md) 0;
}

.mobile-intro-text {
  font-size: var(--mobile-font-base);
  color: var(--mobile-text-secondary);
  margin: 0 0 var(--mobile-xl) 0;
  line-height: var(--mobile-line-height);
}

.mobile-notice-box {
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  border-radius: var(--mobile-radius-lg);
  padding: var(--mobile-lg);
  margin-bottom: var(--mobile-xl);
  border-left: 3px solid var(--fh-accent, #e60023);
}

.mobile-notice-box p {
  font-size: var(--mobile-font-sm);
  color: var(--fh-text-secondary, #a89cc8);
  margin: 0;
  line-height: var(--mobile-line-height);
}

.mobile-dimension-list {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-md);
  margin-bottom: var(--mobile-xxl);
}

.mobile-dimension-item {
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-md) var(--mobile-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobile-dim-name {
  font-weight: 600;
  font-size: var(--mobile-font-base);
  color: var(--fh-accent, #e60023);
}

.mobile-dim-poles {
  display: flex;
  align-items: center;
  gap: var(--mobile-sm);
  font-size: var(--mobile-font-sm);
  color: var(--fh-text-muted, #7a6fa0);
}

.mobile-vs {
  color: var(--fh-accent, #e60023);
  font-weight: 700;
}

.mobile-start-btn {
  display: block;
  width: 100%;
  padding: 14px 0;
  background: var(--fh-accent, #e60023);
  color: #fff;
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  border: none;
  border-radius: var(--mobile-radius-lg);
  cursor: pointer;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px;
}

.mobile-start-btn:active {
  opacity: 0.85;
}

/* -- 移动端答题页 -- */
.mobile-testing {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* 进度条头部 */
.mobile-progress-header {
  padding: var(--mobile-lg) var(--mobile-xl) var(--mobile-sm);
  flex-shrink: 0;
}

.mobile-progress-text {
  font-size: var(--mobile-font-sm);
  color: var(--fh-text-muted, #7a6fa0);
  margin-bottom: var(--mobile-xs);
}

.mobile-progress-bar {
  height: 6px;
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: var(--mobile-sm);
}

.mobile-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--fh-accent, #e60023), #ff4757);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.mobile-dimension-tag {
  display: inline-block;
  font-size: var(--mobile-font-base);
  color: var(--fh-accent, #e60023);
  font-weight: 700;
  background: var(--fh-accent-bg, rgba(124, 58, 237, 0.15));
  padding: 4px 16px;
  border-radius: 20px;
  border: 1px solid var(--fh-accent-light, rgba(124, 58, 237, 0.3));
}

/* 题目区域 */
.mobile-question-area {
  flex: 1;
  padding: var(--mobile-xl) var(--mobile-xl) 140px;
  overflow-y: auto;
}

.mobile-question-content {
  max-width: 400px;
  margin: 0 auto;
}

.mobile-question-number {
  display: inline-block;
  background: var(--fh-accent, #e60023);
  color: #fff;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: var(--mobile-font-sm);
  font-weight: 600;
  margin-bottom: var(--mobile-lg);
}

.mobile-question-text {
  font-size: var(--mobile-font-md);
  color: var(--fh-text-bright, #fff);
  text-align: center;
  margin: 0 auto var(--mobile-xxl);
  line-height: 1.6;
  min-height: 60px;
}

/* 选项标签 — A/B用不同色调区分方向 */
.mobile-option-label {
  font-size: 14px;
  color: var(--fh-text-bright, #fff);
  text-align: center;
  padding: var(--mobile-sm) var(--mobile-md);
  border-radius: var(--mobile-radius-md);
  line-height: 1.5;
  font-weight: 500;
  border: 1px solid transparent;
}

.mobile-option-a {
  margin-bottom: var(--mobile-lg);
  background: rgba(124, 58, 237, 0.18);
  border-color: rgba(124, 58, 237, 0.35);
  color: #c4b5fd;
}

.mobile-option-b {
  margin-top: var(--mobile-lg);
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.35);
  color: #93c5fd;
}

/* 5档选择按钮 */
.mobile-scale-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--mobile-sm) 0;
  position: relative;
}

/* A← →B 方向提示线 */
.mobile-scale-buttons::before {
  content: '← A          B →';
  position: absolute;
  bottom: -18px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 11px;
  color: var(--fh-text-dim, #6b7280);
  letter-spacing: 0.5px;
}

.mobile-scale-btn {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 2px solid var(--fh-border, rgba(255,255,255,0.2));
  background: var(--fh-white-overlay-soft, rgba(255,255,255,0.06));
  font-size: var(--mobile-font-xs);
  color: var(--fh-text-secondary, #a89cc8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  padding: 0;
  line-height: 1.2;
  text-align: center;
}

.mobile-scale-btn:active {
  transform: scale(0.92);
}

.mobile-scale-btn.is-selected {
  border-color: var(--fh-accent, #e60023);
  background: var(--fh-accent, #e60023);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 12px rgba(230, 0, 35, 0.3);
}

/* 固定底部导航 */
.mobile-nav-bar {
  position: fixed;
  bottom: calc(var(--mobile-tabbar-height) + var(--mobile-safe-bottom));
  left: 0;
  right: 0;
  display: flex;
  padding: var(--mobile-md) var(--mobile-xl);
  background: var(--fh-surface, #1a0435);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid var(--fh-border, rgba(255,255,255,0.2));
  gap: var(--mobile-md);
  z-index: 100;
}

.mobile-nav-btn {
  flex: 1;
  padding: 12px 0;
  border: none;
  border-radius: var(--mobile-radius-lg);
  font-size: var(--mobile-font-base);
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
}

.mobile-nav-btn:active {
  opacity: 0.8;
}

.mobile-nav-prev {
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  color: var(--fh-text-secondary, #a89cc8);
}

.mobile-nav-prev:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.mobile-nav-next {
  background: var(--fh-accent, #e60023);
  color: #fff;
}

.mobile-nav-next:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* -- 移动端结果页 -- */
.mobile-result {
  padding: var(--mobile-xxl) var(--mobile-xl);
}

.mobile-result-type-badge {
  text-align: center;
  font-size: 56px;
  font-weight: 800;
  color: var(--fh-accent, #e60023);
  letter-spacing: 4px;
  margin-bottom: var(--mobile-sm);
}

.mobile-result-subtitle {
  text-align: center;
  font-size: var(--mobile-font-base);
  color: var(--fh-text-muted, #7a6fa0);
  margin: 0 0 var(--mobile-xxl) 0;
}

.mobile-result-notice {
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  border-radius: var(--mobile-radius-lg);
  padding: var(--mobile-lg);
  margin-bottom: var(--mobile-xxl);
}

.mobile-result-notice p {
  font-size: var(--mobile-font-sm);
  color: var(--fh-text-secondary, #a89cc8);
  margin: 0;
  line-height: var(--mobile-line-height);
}

/* 维度强度条 */
.mobile-dimension-bars {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-lg);
  margin-bottom: var(--mobile-xxl);
}

.mobile-dim-bar-item {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-xs);
}

.mobile-dim-bar-labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--mobile-font-sm);
  color: var(--fh-text-muted, #7a6fa0);
  font-weight: 500;
}

.mobile-dim-bar-track {
  height: 8px;
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  border-radius: 4px;
  overflow: hidden;
}

.mobile-dim-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--fh-accent, #e60023), #ff6b81);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.mobile-dim-bar-percent {
  text-align: right;
  font-size: var(--mobile-font-xs);
  color: var(--fh-text-muted, #7a6fa0);
}

.mobile-result-actions {
  margin-top: var(--mobile-xl);
}

.mobile-retake-btn {
  display: block;
  width: 100%;
  padding: 14px 0;
  background: var(--fh-white-overlay, rgba(255,255,255,0.1));
  color: var(--fh-accent, #e60023);
  font-size: var(--mobile-font-base);
  font-weight: 600;
  border: 2px solid var(--fh-accent, #e60023);
  border-radius: var(--mobile-radius-lg);
  cursor: pointer;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px;
}

.mobile-retake-btn:active {
  opacity: 0.8;
}

.mobile-poster-btn {
  display: block;
  width: 100%;
  padding: 14px 0;
  margin-top: var(--mobile-md);
  background: var(--fh-accent, #e60023);
  color: #fff;
  font-size: var(--mobile-font-base);
  font-weight: 600;
  border: none;
  border-radius: var(--mobile-radius-lg);
  cursor: pointer;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px;
}

.mobile-poster-btn:active {
  opacity: 0.8;
}

/* ========== 题目切换过渡动画 ========== */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.25s ease;
}

.slide-left-enter-from {
  transform: translateX(40px);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-40px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(40px);
  opacity: 0;
}
</style>
